import { z } from "zod";

/**
 * ISO-8601 timestamps arriving from a caller.
 *
 * `z.coerce.date()` was the idiom for these and it is wrong on any boundary
 * schema, for two independent reasons:
 *
 * 1. **It accepts values that are not timestamps.** `new Date(...)` is a
 *    best-effort parser, so `0` and `false` both become 1970-01-01, the string
 *    `"0"` becomes 2000-01-01, and `"05/15/2026"` is read month-first
 *    regardless of what the caller meant. A caller that mis-serialises a field
 *    gets a well-formed filter over the wrong window instead of a 400 — the
 *    failure is silent and the result looks legitimate.
 * 2. **It has no JSON Schema representation.** `z.toJSONSchema` renders a
 *    coercion as `{}`: a schema that constrains nothing and, worse, tells a
 *    reader nothing. Every route carrying one of these params documents it as
 *    an "ISO timestamp" in its OpenAPI annotation while the schema an MCP tool
 *    catalog or client generator actually reads said nothing at all.
 *
 * The line drawn here is **ISO 8601 / RFC 3339, in full**. Every spelling of an
 * instant that standard permits is accepted, including the ones that differ
 * from Zod's strict forms only in punctuation — a space separator, a
 * colon-less offset, lowercase designators, reduced precision. Those are
 * canonicalised below rather than reimplemented, so the validation itself
 * stays with Zod and this file never becomes a second date parser.
 *
 * What is deliberately NOT accepted, though `new Date()` would take it:
 * locale and RFC-2822 forms (`05/15/2026`, `May 15 2026`,
 * `Fri, 15 May 2026 00:00:00 GMT`). `05/15/2026` is genuinely ambiguous — it
 * means two different days on two continents and `new Date()` silently picks
 * one — and admitting the unambiguous members of that family means owning a
 * bespoke parser for month names. Every affected route already documents "ISO
 * timestamp", so this narrows the implementation to the published contract
 * rather than narrowing the contract.
 */

/**
 * Timestamps without an offset are read as UTC, never as the server's local
 * time. Two reasons this is not merely a preference: the calendar-date form
 * below is already UTC midnight, so a local-time reading would make one
 * parameter mean two different things depending on which form the caller sent;
 * and a contract whose meaning depends on the deployment's `TZ` is not a
 * contract. `new Date("2026-05-15T00:00:00")` is local-time by ECMA-262, so the
 * offset is appended explicitly rather than left to the runtime. (Every
 * deployed image already resolves to UTC, so this changes no production result
 * — it removes the dependency, not a behaviour.)
 */
const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:\d{2})$/;

/** Length of a bare `YYYY-MM-DD` calendar date. */
const CALENDAR_DATE_LENGTH = 10;

/** `YYYY` — ISO 8601 reduced precision, meaning the start of that year. */
const YEAR_ONLY = /^\d{4}$/;

/** `YYYY-MM` — ISO 8601 reduced precision, meaning the start of that month. */
const YEAR_MONTH = /^\d{4}-\d{2}$/;

/** A date and a time joined by a space (RFC 3339 §5.6 permits this) rather than `T`. */
const SPACE_SEPARATED = /^(\d{4}-\d{2}-\d{2}) (\d{2}:.*)$/;

/** A trailing offset in ISO basic format (`+0200`) or hour-only (`+02`). */
const COMPACT_OFFSET = /([+-])(\d{2})(\d{2})?$/;

const ISO_DATE_TIME_DESCRIPTION =
  "An ISO-8601 timestamp. Accepts a date-time with an offset " +
  "(`2026-05-15T00:00:00Z`, `2026-05-15T02:00:00+02:00`, `2026-05-15T02:00:00+0200`), " +
  "a date-time without one (`2026-05-15T00:00:00`, read as UTC), a date and time " +
  "separated by a space instead of `T`, a calendar date (`2026-05-15`, read as midnight " +
  "UTC), or a reduced-precision `2026-05` / `2026` (read as the start of that month or " +
  "year). Locale and RFC-2822 spellings such as `05/15/2026` or " +
  "`Fri, 15 May 2026 00:00:00 GMT` are rejected — `05/15/2026` means a different day " +
  "depending on the reader.";

/**
 * Rewrite the ISO-8601 spellings that differ from Zod's strict forms only in
 * punctuation or precision.
 *
 * Purely syntactic: it never decides whether a value is a valid instant. A
 * canonicalised string still has to pass `z.iso.*` below, so a nonsense input
 * that survives this function (`2026-13-99 25:00:00`) is rejected there rather
 * than being coerced into something plausible.
 */
function canonicaliseIso(value: string): string {
  let result = value.trim();

  // Reduced precision → the first instant of the period it names, matching how
  // `new Date("2026")` and `new Date("2026-05")` already read them.
  if (YEAR_ONLY.test(result)) return `${result}-01-01`;
  if (YEAR_MONTH.test(result)) return `${result}-01`;

  result = result.replace(SPACE_SEPARATED, "$1T$2");

  // ISO 8601 permits lowercase designators. Only the separator and the zone
  // marker are touched — never the date or time digits.
  if (result.length > CALENDAR_DATE_LENGTH && result[CALENDAR_DATE_LENGTH] === "t") {
    result = `${result.slice(0, CALENDAR_DATE_LENGTH)}T${result.slice(CALENDAR_DATE_LENGTH + 1)}`;
  }
  if (result.endsWith("z")) result = `${result.slice(0, -1)}Z`;

  // `+0200` / `+02` → `+02:00`. Anchored to a value that already has a time, so
  // the `-` inside a bare `2026-05-15` can never be mistaken for an offset sign.
  if (result.includes("T")) {
    result = result.replace(
      COMPACT_OFFSET,
      (_match, sign: string, hours: string, minutes = "00") =>
        minutes.length === 0 ? `${sign}${hours}:00` : `${sign}${hours}:${minutes}`
    );
  }

  return result;
}

/**
 * An ISO-8601 date-time or calendar date, as a string — and nothing else.
 *
 * Both forms are accepted because both were already reaching these parameters
 * through `z.coerce.date()`; narrowing to date-time only would reject callers
 * that work today.
 */
export const IsoDateTimeStringSchema = z.preprocess(
  (value) => (typeof value === "string" ? canonicaliseIso(value) : value),
  z.union([z.iso.datetime({ offset: true, local: true }), z.iso.date()])
);

/** A string this API will accept as an ISO-8601 timestamp. */
export type IsoDateTimeString = z.input<typeof IsoDateTimeStringSchema>;

/** Parse a canonicalised ISO-8601 string to a `Date`, resolving it in UTC. */
function toUtcDate(value: string): Date {
  if (value.length === CALENDAR_DATE_LENGTH) return new Date(`${value}T00:00:00Z`);
  return new Date(EXPLICIT_OFFSET.test(value) ? value : `${value}Z`);
}

/**
 * A wire-facing timestamp parameter: an ISO-8601 string in, a `Date` out.
 *
 * Use this for anything a caller sends over HTTP — a query parameter, a request
 * body field. It converts to `{"anyOf": [{"type": "string", "format":
 * "date-time"}, {"type": "string", "format": "date"}], "description": …}`, so
 * what the route documents and what the schema advertises are the same thing.
 */
export const IsoDateTimeParamSchema =
  IsoDateTimeStringSchema.transform(toUtcDate).describe(ISO_DATE_TIME_DESCRIPTION);

/**
 * A timestamp from either an in-process caller (already a `Date`) or the wire
 * (an ISO-8601 string).
 *
 * Use this only on a schema that genuinely has both kinds of caller — a shared
 * filter set parsed by a workflow node with typed values AND by an HTTP layer.
 * It is deliberately NOT the default: `z.date()` is unrepresentable in JSON
 * Schema, so a slot reachable from the MCP catalog must use
 * `IsoDateTimeParamSchema` instead.
 */
export const DateOrIsoDateTimeSchema = z
  .union([z.date(), IsoDateTimeParamSchema])
  .describe(ISO_DATE_TIME_DESCRIPTION);
